import { ampli } from '@/ampli';
import log from '@/lib/logger';
import { type AppDispatch, type RootState } from '@/store';
import { type NormalizedConnectorResponse } from '@/types/client';
import { type UnknownAction } from '@reduxjs/toolkit';
import { type AxiosError, type AxiosResponse } from 'axios';
import { http } from 'config';
import { loadChargingProfiles } from 'features/ChargingProfiles';
import { showSnackbarMessage } from 'features/Common';
import { connectorListSchema, connectorSchema } from 'features/schemas';
import { loadTransactions } from 'features/Transactions';
import { loadVehicles } from 'features/Vehicles';
import _ from 'lodash';
import { normalize } from 'normalizr';
import Nprogress from 'nprogress';
import { getConnectors, getOnlineConnectorsByGridConnectionId } from 'services/connectors';
import { ChargePointStatusEnum, type Connector, type SampledValue } from 'types/swagger';
import { callNextPage } from 'util/callNextPage';
import { getNewImmutableState } from 'util/getNewImmutableState';

import { unselectChargePoint } from './ChargePoints';

// --------------------------------- Actions ---------------------------------
// Action Types
export enum ConnectorActionTypes {
  LOAD_CONNECTORS = 'connectors/load_connectors',
  SET_CONNECTORS_BY_GRID_CONNECTION_ID = 'connectors/set_connectors_by_grid_connection_id',
  LOAD_CONNECTORS_BY_GRID_CONNECTION_ID = 'connectors/load_connectors_by_grid_connection_id',
  LOAD_ONLINE_CONNECTORS_BY_GRID_CONNECTION_ID = 'connectors/load_online_connectors_by_grid_connection_id',
  LOAD_POWER_VALUES = 'connectors/load_power_values',
  SELECT_CONNECTOR = 'connectors/select_connector',
  UNSELECT_CONNECTORS = 'connectors/unselect_connectors',
  CONNECTORS_FETCH_START = 'connectors/connectors_fetch_start',
  CONNECTORS_FETCH_END = 'connectors/connectors_fetch_end',
  UPDATE_CONNECTOR_START = 'connectors/update_connector_start',
  UPDATE_CONNECTOR_END = 'connectors/update_connector_end',
  POWER_FETCH_START = 'connectors/power_fetch_start',
  POWER_FETCH_END = 'connectors/power_fetch_end',
  TOGGLE_START = 'connectors/toggle_start',
  TOGGLE_END = 'connectors_/toggle_end',
  SET_SELECTED_CONNECTOR = 'connectors/set_selected_connector',
  LOAD_CONNECTORS_ONLINE = 'connectors/load_connectors_online',
  SET_CONNECTORS_ONLINE = 'connectors/set_connectors',
  SET_CONNECTORS_ONLINE_BY_GRID_CONNECTION = 'connectors/set_connectors_online_by_grid_connection',
  REMOVE_CONNECTOR_BY_ID = 'connectors/remove_connector_by_id',
  REMOVE_CONNECTOR_BY_GRID_CONNECTION_ID = 'connectors/remove_connector_by_grid_connection_id',
  ADD_DESYNCED_CHARGING_CONNECTOR_ID = 'connectors/add_desynced_charging_connector_id',
  REMOVE_DESYNCED_CHARGING_CONNECTOR_ID = 'connectors/remove_desynced_charging_connector_id',
}
// Action Creators
export const loadConnectors = (connectors: Record<number, Connector>) => ({
  type: ConnectorActionTypes.LOAD_CONNECTORS,
  payload: connectors,
});
export const setConnectorsByGridConnectionId = (
  gridConnectionId: number,
  connectors: Record<number, Connector>
) => ({
  type: ConnectorActionTypes.SET_CONNECTORS_BY_GRID_CONNECTION_ID,
  meta: { gridConnectionId },
  payload: connectors,
});
export const loadConnectorsByGridConnectionId = (
  gridConnectionId: number,
  connectors: Record<number, Connector>
) => ({
  type: ConnectorActionTypes.LOAD_CONNECTORS_BY_GRID_CONNECTION_ID,
  meta: { gridConnectionId },
  payload: connectors,
});
export const loadOnlineConnectorsByGridConnectionId = (
  gridConnectionId: number,
  connectors: Record<number, Connector>
) => ({
  type: ConnectorActionTypes.LOAD_ONLINE_CONNECTORS_BY_GRID_CONNECTION_ID,
  meta: { gridConnectionId },
  payload: connectors,
});
export const loadPowerValues = (
  connectorId: number,
  start: string,
  stop: string,
  values: SampledValue[]
) => ({
  type: ConnectorActionTypes.LOAD_POWER_VALUES,
  meta: { connectorId, timestamp_start: start, timestamp_stop: stop },
  payload: values,
});
// export const selectConnector = (connectorId) => ({ type: SELECT_CONNECTOR, payload: connectorId });
export const unselectConnectors = () => ({ type: ConnectorActionTypes.UNSELECT_CONNECTORS });
export const connectorsFetchStart = () => ({ type: ConnectorActionTypes.CONNECTORS_FETCH_START });
export const connectorsFetchEnd = () => ({ type: ConnectorActionTypes.CONNECTORS_FETCH_END });
export const updateConnectorStart = () => ({ type: ConnectorActionTypes.UPDATE_CONNECTOR_START });
export const updateConnectorEnd = () => ({ type: ConnectorActionTypes.UPDATE_CONNECTOR_END });
export const powerFetchStart = () => ({ type: ConnectorActionTypes.POWER_FETCH_START });
export const powerFetchEnd = () => ({ type: ConnectorActionTypes.POWER_FETCH_END });
export const toggleStart = () => ({ type: ConnectorActionTypes.TOGGLE_START });
export const toggleEnd = () => ({ type: ConnectorActionTypes.TOGGLE_END });

export const setSelectedConnector = (connector: Connector | null) => ({
  type: ConnectorActionTypes.SET_SELECTED_CONNECTOR,
  payload: connector,
});
export const loadConnectorsOnline = (connectors: Record<number, Connector>) => ({
  type: ConnectorActionTypes.LOAD_CONNECTORS_ONLINE,
  payload: connectors,
});
export const setConnectorsOnline = (connectors: Record<number, Connector>) => ({
  type: ConnectorActionTypes.SET_CONNECTORS_ONLINE,
  payload: connectors,
});
export const setOnlineConnectorsByGridConnectionId = (
  gridConnectionId: number,
  connectors: Record<number, Connector>
) => ({
  type: ConnectorActionTypes.SET_CONNECTORS_ONLINE_BY_GRID_CONNECTION,
  meta: { gridConnectionId },
  payload: connectors,
});
export const removeConnectorById = (connectorId: number) => ({
  type: ConnectorActionTypes.REMOVE_CONNECTOR_BY_ID,
  meta: { connectorId },
});
export const removeConnectorByGridConnectionId = (
  gridConnectionId: number,
  connectorId: number
) => ({
  type: ConnectorActionTypes.REMOVE_CONNECTOR_BY_GRID_CONNECTION_ID,
  meta: { gridConnectionId, connectorId },
});

export const addDesyncedChargingConnector = (id: number) => ({
  type: ConnectorActionTypes.ADD_DESYNCED_CHARGING_CONNECTOR_ID,
  payload: id,
});
export const removeDesyncedChargingConnector = (id: number) => ({
  type: ConnectorActionTypes.REMOVE_DESYNCED_CHARGING_CONNECTOR_ID,
  payload: id,
});

export interface ConnectorsState {
  byId: Record<number, Connector>;
  byGridConnectionId: Record<number, Record<number, Connector>>;
  onlineById: Record<number, Connector>;
  onlineByGridConnectionId: Record<number, Record<number, Connector>>;
  powerValuesByConnectorByTimestamp: Record<string, SampledValue[]>;
  allIds: number[];
  selectedConnectorId: number | null;
  selectedConnector: Connector | null;
  fetching: boolean;
  powerFetching: boolean;
  toggling: boolean;
  desyncedChargingConnectorIds: number[];
}

// ----------------------------------- Reducer ---------------------------------
// Init State
const INIT_STATE: ConnectorsState = {
  byId: {},
  byGridConnectionId: {},
  onlineById: {},
  onlineByGridConnectionId: {},
  powerValuesByConnectorByTimestamp: {},
  allIds: [],
  selectedConnectorId: null,
  selectedConnector: null,
  fetching: false,
  powerFetching: false,
  toggling: false,
  desyncedChargingConnectorIds: [],
};

const connectorReducer = (state = INIT_STATE, action: UnknownAction): ConnectorsState => {
  const meta = action.meta;
  let payload = action.payload;
  switch (action.type) {
    case ConnectorActionTypes.CONNECTORS_FETCH_START: {
      return { ...state, fetching: true };
    }
    case ConnectorActionTypes.CONNECTORS_FETCH_END: {
      return { ...state, fetching: false };
    }
    case ConnectorActionTypes.POWER_FETCH_START: {
      return { ...state, powerFetching: true };
    }
    case ConnectorActionTypes.POWER_FETCH_END: {
      return { ...state, powerFetching: false };
    }
    case ConnectorActionTypes.TOGGLE_START: {
      return { ...state, toggling: true };
    }
    case ConnectorActionTypes.TOGGLE_END: {
      return { ...state, toggling: false };
    }
    case ConnectorActionTypes.LOAD_CONNECTORS: {
      const connectorsById = payload as Record<number, Connector>;
      const desyncedChargingConnectorIds: number[] = [];
      connectorsById &&
        Object.values(connectorsById).forEach((connector) => {
          const isCharging =
            (connector?.latest_status_notification?.status as unknown as ChargePointStatusEnum) ===
              ChargePointStatusEnum.Charging ||
            (connector?.latest_status_notification?.status as unknown as ChargePointStatusEnum) ===
              ChargePointStatusEnum.Finishing;
          if (isCharging && !connector?.latest_meter_value?.transaction)
            desyncedChargingConnectorIds.push(connector?.id);
        });
      return {
        ...state,
        byId: {
          ...state.byId,
          ...connectorsById,
        },
        desyncedChargingConnectorIds: [
          ...state.desyncedChargingConnectorIds,
          ...desyncedChargingConnectorIds,
        ],
      };
    }
    case ConnectorActionTypes.SET_CONNECTORS_BY_GRID_CONNECTION_ID: {
      const metadata = meta as { gridConnectionId: number };
      return getNewImmutableState(
        state,
        { [metadata.gridConnectionId]: payload },
        'byGridConnectionId',
        true
      );
    }
    case ConnectorActionTypes.LOAD_CONNECTORS_BY_GRID_CONNECTION_ID: {
      const metadata = meta as { gridConnectionId: number };
      return getNewImmutableState(
        state,
        {
          [metadata.gridConnectionId]: {
            ...state.byGridConnectionId[metadata.gridConnectionId],
            ...(payload as Connector[]),
          },
        },
        'byGridConnectionId',
        true
      );
    }
    case ConnectorActionTypes.LOAD_ONLINE_CONNECTORS_BY_GRID_CONNECTION_ID: {
      const metadata = meta as { gridConnectionId: number };
      payload = payload as Connector[];
      return {
        ...state,
        onlineByGridConnectionId: {
          ...state.onlineByGridConnectionId,
          [metadata.gridConnectionId]: {
            ...state.onlineByGridConnectionId[metadata.gridConnectionId],
            ...(payload as Connector[]),
          },
        },
      };
    }
    case ConnectorActionTypes.LOAD_POWER_VALUES: {
      const metadata = meta as {
        connectorId: number;
        timestamp_start: string;
        timestamp_stop: string;
      };
      return getNewImmutableState(
        state,
        {
          [`${metadata.connectorId}-${metadata.timestamp_start}-${metadata.timestamp_stop}`]:
            payload,
        },
        'powerValuesByConnectorByTimestamp',
        true
      );
    }
    case ConnectorActionTypes.SELECT_CONNECTOR: {
      return { ...state, selectedConnectorId: payload as number };
    }
    case ConnectorActionTypes.UNSELECT_CONNECTORS: {
      return { ...state, selectedConnectorId: null };
    }
    case ConnectorActionTypes.SET_SELECTED_CONNECTOR: {
      if (payload !== null) ampli.connectorSelected();
      return { ...state, selectedConnector: payload as Connector };
    }
    case ConnectorActionTypes.LOAD_CONNECTORS_ONLINE: {
      return {
        ...state,
        onlineById: { ...state.onlineById, ...(payload as Record<number, Connector>) },
      };
    }
    case ConnectorActionTypes.SET_CONNECTORS_ONLINE: {
      return {
        ...state,
        onlineById: payload as Record<number, Connector>,
      };
    }
    case ConnectorActionTypes.SET_CONNECTORS_ONLINE_BY_GRID_CONNECTION: {
      const metadata = meta as { gridConnectionId: number };
      return {
        ...state,
        onlineByGridConnectionId: {
          ...state.onlineByGridConnectionId,
          [metadata.gridConnectionId]: payload as Record<number, Connector>,
        },
      };
    }
    case ConnectorActionTypes.REMOVE_CONNECTOR_BY_ID: {
      const metadata = meta as { connectorId: number };
      const newState = _.cloneDeep(state);
      delete newState.byId[metadata.connectorId];
      delete newState.onlineById[metadata.connectorId];
      return newState;
    }
    // * Case to remove onboarding connectors
    case ConnectorActionTypes.REMOVE_CONNECTOR_BY_GRID_CONNECTION_ID: {
      const metadata = meta as { gridConnectionId: number; connectorId: number };
      const newState = _.cloneDeep(state.byGridConnectionId);
      const newState2 = _.cloneDeep(state.onlineByGridConnectionId);
      if (newState?.[metadata?.gridConnectionId]?.[metadata?.connectorId]) {
        const { ...otherConnectors } = newState[metadata.gridConnectionId];
        newState[metadata.gridConnectionId] = otherConnectors;
      }
      if (newState2?.[metadata?.gridConnectionId]?.[metadata?.connectorId]) {
        const { ...otherConnectorsOnline } = newState2[metadata.gridConnectionId];
        newState2[metadata.gridConnectionId] = otherConnectorsOnline;
      }
      return {
        ...state,
        byGridConnectionId: newState,
        onlineByGridConnectionId: newState2,
      };
    }
    case ConnectorActionTypes.ADD_DESYNCED_CHARGING_CONNECTOR_ID: {
      return {
        ...state,
        desyncedChargingConnectorIds: [...state.desyncedChargingConnectorIds, payload as number],
      };
    }
    case ConnectorActionTypes.REMOVE_DESYNCED_CHARGING_CONNECTOR_ID: {
      return {
        ...state,
        desyncedChargingConnectorIds: state.desyncedChargingConnectorIds.filter(
          (id) => id !== payload
        ),
      };
    }
    default:
      return state;
  }
};

export default connectorReducer;

// ----------------------------------- Side Effects ----------------------------

export const fetchAllConnectorsOnlineByGridConnection =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    const currentGridConnections = getState().gridConnections.byId;
    const promises: Array<Promise<AxiosResponse<{ results: Connector[] }>>> = [];
    Object.values(currentGridConnections).forEach((gridConnection) => {
      promises.push(getOnlineConnectorsByGridConnectionId(gridConnection.id));
    });

    return await Promise.all(promises)
      .then((responses) => {
        const connectorsById: Record<number, Connector> = {};
        responses.forEach((response) => {
          response.data.results.forEach((connector) => {
            connectorsById[connector.id] = connector;
          });
        });
        dispatch(loadConnectorsOnline(connectorsById));
        return responses;
      })
      .catch((e) => {
        log.error(e);
        return e;
      });
  };

export const fetchConnectors = () => async (dispatch: AppDispatch) => {
  dispatch(connectorsFetchStart());
  return await getConnectors()
    .then((response) => {
      const normalizedData = normalize(response, connectorListSchema);
      const { connectors } = normalizedData.entities;
      if (connectors) dispatch(loadConnectors(connectors));
      dispatch(connectorsFetchEnd());
      return normalizedData.entities;
    })
    .catch((error) => {
      log.error(error);
      dispatch(connectorsFetchEnd());
      return error;
    });
};

export const fetchConnectorsByGridConnectionId =
  (gridConnectionId: number) => async (dispatch: AppDispatch) => {
    dispatch(connectorsFetchStart());
    Nprogress.start();
    const apiConnectors = `chargepoints/connectors/by_grid_connection/${gridConnectionId}/?page_size=999`;
    const resolve = (data: { results: Connector[] }) => {
      const normalizedData: NormalizedConnectorResponse = normalize(data, connectorListSchema);
      const {
        connectors,
        charging_profiles: chargingProfiles,
        transactions,
        vehicles,
      } = normalizedData.entities;
      if (connectors) {
        dispatch(loadConnectors(connectors));
        dispatch(setConnectorsByGridConnectionId(gridConnectionId, connectors));
      }
      if (transactions) dispatch(loadTransactions(transactions));
      if (vehicles) dispatch(loadVehicles(vehicles));
      if (chargingProfiles) dispatch(loadChargingProfiles(chargingProfiles));
      dispatch(connectorsFetchEnd());
      Nprogress.done();
      return data;
    };
    const reject = (error: AxiosError) => {
      log.error(error);
      dispatch(connectorsFetchEnd());
      Nprogress.done();
      return error;
    };
    return callNextPage(apiConnectors, resolve, reject);
  };

export const fetchOnlineConnectorsByGridConnectionId =
  (gridConnectionId: number) => async (dispatch: AppDispatch) => {
    // dispatch(connectorsFetchStart());
    // Nprogress.start();
    const apiConnectors = `chargepoints/connectors/by_grid_connection/${gridConnectionId}/online/?page_size=999`;
    const resolve = (data: { results: Connector[] }) => {
      const normalizedData: NormalizedConnectorResponse = normalize(data, connectorListSchema);
      const {
        connectors,
        charging_profiles: chargingProfiles,
        transactions,
        vehicles,
      } = normalizedData.entities;
      if (connectors) {
        dispatch(loadConnectorsOnline(connectors));
        dispatch(loadOnlineConnectorsByGridConnectionId(gridConnectionId, connectors));
      }
      if (transactions) dispatch(loadTransactions(transactions));
      if (vehicles) dispatch(loadVehicles(vehicles));
      if (chargingProfiles) dispatch(loadChargingProfiles(chargingProfiles));
      // dispatch(connectorsFetchEnd());
      // Nprogress.done();
      return data;
    };
    const reject = (error: AxiosError) => {
      log.error(error);
      // dispatch(connectorsFetchEnd());
      // Nprogress.done();
      return error;
    };
    return callNextPage(apiConnectors, resolve, reject);
  };

export const fetchConnector = (connectorId: number) => async (dispatch: AppDispatch) => {
  const apiConnectors = `chargepoints/connectors/${connectorId}/`;
  dispatch(connectorsFetchStart());
  return await http
    .get(apiConnectors)
    .then((response) => {
      const normalizedData = normalize(response.data, connectorSchema);
      const {
        connectors,
        charging_profiles: chargingProfiles,
        transactions,
        vehicles,
      } = normalizedData.entities;
      if (connectors) dispatch(loadConnectors(connectors));
      if (transactions) dispatch(loadTransactions(transactions));
      if (vehicles) dispatch(loadVehicles(vehicles));
      if (chargingProfiles) dispatch(loadChargingProfiles(chargingProfiles));
      dispatch(connectorsFetchEnd());
      return normalizedData.entities;
    })
    .catch((error) => {
      log.error(error);
      dispatch(connectorsFetchEnd());
      return error;
    });
};

export const fetchUpdatedConnector =
  (newConnector: Connector) => async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(connectorsFetchStart());
    try {
      // const prevConnector = getState().connectors.byId[newConnector.id];
      const connectorChargePoint = getState().chargePoints.byId[newConnector.charge_point];
      // connectorPushNotifications(newConnector, prevConnector, connectorChargePoint);
      const normalizedData: NormalizedConnectorResponse = normalize(newConnector, connectorSchema);
      const {
        connectors,
        charging_profiles: chargingProfiles,
        transactions,
        vehicles,
      } = normalizedData.entities;
      if (connectors) {
        const selectedConnector = getState().connectors.selectedConnector;
        if (selectedConnector && selectedConnector.id === newConnector.id)
          dispatch(setSelectedConnector(Object.values(connectors)[0]));
        dispatch(loadConnectors(connectors));
        if (connectorChargePoint) {
          if (connectorChargePoint.grid_connection)
            dispatch(
              loadConnectorsByGridConnectionId(connectorChargePoint.grid_connection, connectors)
            );

          if (
            connectorChargePoint.status === 'Online' ||
            connectorChargePoint.status === 'Waiting'
          ) {
            dispatch(loadConnectorsOnline(connectors));
            if (connectorChargePoint.grid_connection) {
              dispatch(
                loadOnlineConnectorsByGridConnectionId(
                  connectorChargePoint.grid_connection,
                  connectors
                )
              );
            }
          }
        }
      }
      if (transactions) dispatch(loadTransactions(transactions));
      if (vehicles) dispatch(loadVehicles(vehicles));
      if (chargingProfiles) dispatch(loadChargingProfiles(chargingProfiles));
      dispatch(connectorsFetchEnd());
      return normalizedData.entities;
    } catch (error) {
      log.error(error);
      dispatch(connectorsFetchEnd());
      return error;
    }
  };

export const patchConnector = (connector: Partial<Connector>) => async (dispatch: AppDispatch) => {
  Nprogress.start();
  const apiConnectors = `chargepoints/connectors/${connector.id}/`;
  dispatch(updateConnectorStart());
  return await http
    .patch(apiConnectors, connector)
    .then((response) => {
      const normalizedData: NormalizedConnectorResponse = normalize(response.data, connectorSchema);
      const {
        connectors,
        charging_profiles: chargingProfiles,
        transactions,
        vehicles,
      } = normalizedData.entities;
      if (connectors) dispatch(loadConnectors(connectors));
      if (transactions) dispatch(loadTransactions(transactions));
      if (vehicles) dispatch(loadVehicles(vehicles));
      if (chargingProfiles) dispatch(loadChargingProfiles(chargingProfiles));
      dispatch(showSnackbarMessage('Opciones del conector actualizadas exitosamente.'));
      dispatch(updateConnectorEnd());
      Nprogress.done();
      return response.data;
    })
    .catch((error) => {
      dispatch(showSnackbarMessage('No se ha podido completar la acción.'));
      dispatch(updateConnectorEnd());
      log.error(error);
      Nprogress.done();
      return error;
    });
};

export const fetchSampleValue = (sampleValueId: number) => async () => {
  const apiConnectors = `chargepoints/sampled_values/${sampleValueId}/`;
  return await http
    .get(apiConnectors)
    .then((response) => response.data)
    .catch((error) => {
      log.error(error);
      return error;
    });
};

export const selectConnector =
  (connectorId: number) => (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const connector = state.connectors.byId[connectorId];
    dispatch(unselectChargePoint());
    dispatch(unselectConnector());
    dispatch(setSelectedConnector(connector));
  };

export const unselectConnector = () => (dispatch: AppDispatch) => {
  dispatch(setSelectedConnector(null));
};

export const getConnectorsSelector = (state: RootState) => state.connectors.byId;
export const selectConnectorById = (state: RootState, connectorId: number) =>
  state.connectors.byId[connectorId];
export const selectAllConnectorsByGridConnectionId = (state: RootState) =>
  state.connectors.byGridConnectionId;
export const selectConnectorsByGridConnectionId = (state: RootState, gridConnectionId: number) =>
  state.connectors.byGridConnectionId[gridConnectionId];
export const selectAllConnectorsOnline = (state: RootState) => state.connectors.onlineById;
export const getConnectorsOnlineByGridConnectionIdSelector = (
  state: RootState,
  gridConnectionId: number
) => state.connectors.onlineByGridConnectionId[gridConnectionId];
export const getSelectedConnectorIdSelector = (state: RootState) =>
  state.connectors.selectedConnectorId;
export const getSelectedConnectorSelector = (state: RootState) =>
  state.connectors.selectedConnector;
export const getDesyncedChargingConnectorIdsSelector = (state: RootState) =>
  state.connectors.desyncedChargingConnectorIds;
